import { combineLatest, map } from 'rxjs';
import {
  action$,
  ofType,
  persistentReducedStream,
  reducer,
  streamReducer,
} from '@ardoq/rxbeach';
import {
  SaveAsViewpointInputsReducer,
  SaveAsViewpointInputsState,
  UpdateSaveAsViewpointStatePayload,
  ViewStyle,
  SaveAsViewpointInputsErrors,
} from './types';
import { resetSaveAsViewpoint, updateSaveAsViewpointState } from './actions';
import { isWorkspaceModule$ } from 'streams/isWorkspaceModule$';
import {
  activeView$,
  ActiveViews,
} from 'streams/views/mainContent/activeView$';
import { viewIdToUserFriendlyName } from 'views/metaInfoConsts';
import { toStoredViewpointViewId } from 'views/environmentals/getViewpointModeSupportedViews';
import { SaveAsViewpointErrorCode } from './consts';
import { startWith } from 'rxjs/operators';
import { startAction } from 'actions/utils';
import {
  openedViewpointData$,
  OpenedViewpointData,
} from './openedViewpointData$';
import { openedViewpointFolderId$ } from './openedViewpointFolderId$';
import { ArdoqId, ViewIds } from '@ardoq/api-types';

const errorsForEmptyNameField = {
  viewpointName: SaveAsViewpointErrorCode.VIEWPOINT_NAME_REQUIRED,
  saveButton: SaveAsViewpointErrorCode.SAVE_BUTTON_NEEDS_NAME,
  updateButton: SaveAsViewpointErrorCode.UPDATE_BUTTON_NEEDS_NAME,
};

const getViewpointInputsError = (
  state: SaveAsViewpointInputsState
): SaveAsViewpointInputsErrors => {
  const { viewpointName } = state;

  const hasName = viewpointName.trim() !== '';

  if (hasName) {
    return {};
  }

  return errorsForEmptyNameField;
};

const activeViewChangedReducer: SaveAsViewpointInputsReducer<ViewStyle> = (
  state,
  viewStyle
) => ({ ...state, ...viewStyle });

const validateState = (
  state: SaveAsViewpointInputsState
): SaveAsViewpointInputsState => ({
  ...state,
  errors: getViewpointInputsError(state),
});

const updateStateReducer: SaveAsViewpointInputsReducer<
  UpdateSaveAsViewpointStatePayload
> = (state, payload) =>
  validateState({
    ...state,
    ...payload,
  });

const defaultEditableState = {
  viewpointName: '',
  viewpointDescription: '',
  folderId: null,
  originalViewpointName: '',
  originalViewpointDescription: '',
  originalFolderId: null,
  errors: errorsForEmptyNameField,
};

const getCurrentView = ({ mainViewId }: ActiveViews) => {
  const storedViewId = toStoredViewpointViewId(mainViewId);
  return {
    mainViewId: storedViewId,
    mainViewName:
      storedViewId === ViewIds.NONE
        ? undefined
        : viewIdToUserFriendlyName[storedViewId],
  };
};

export const getDefaultInputsState = (): SaveAsViewpointInputsState => ({
  ...defaultEditableState,
  // oxlint-disable-next-line ardoq-lint/no-direct-stream-state-access
  ...getCurrentView(activeView$.state),
});

const viewPaneHeader$ = activeView$.pipe(map(getCurrentView));

const resetReducer = (state: SaveAsViewpointInputsState) => {
  const newState = {
    ...state,
    viewpointName: state.originalViewpointName,
    viewpointDescription: state.originalViewpointDescription,
    folderId: state.originalFolderId,
  };
  return validateState(newState);
};

const reset$ = combineLatest([
  isWorkspaceModule$,
  action$.pipe(ofType(resetSaveAsViewpoint), startWith(startAction)),
]);

const updateActiveViewpointReducer = (
  state: SaveAsViewpointInputsState,
  payload: OpenedViewpointData
) => {
  const { openedViewpoint } = payload;

  const viewpointName = openedViewpoint?.name || '';

  const viewpointDescription = openedViewpoint?.description || '';

  const updatedState = {
    ...state,
    viewpointName,
    viewpointDescription,
    originalViewpointName: viewpointName,
    originalViewpointDescription: viewpointDescription,
  };

  return validateState(updatedState);
};

const openedViewpointFolderIdReducer: SaveAsViewpointInputsReducer<
  ArdoqId | null
> = (state, folderId) => ({
  ...state,
  folderId,
  originalFolderId: folderId,
});

export const saveAsViewpointInputs$ = persistentReducedStream(
  'saveAsViewpointInputs$',
  getDefaultInputsState(),
  [
    streamReducer(openedViewpointData$, updateActiveViewpointReducer),
    streamReducer(openedViewpointFolderId$, openedViewpointFolderIdReducer),
    reducer(updateSaveAsViewpointState, updateStateReducer),
    streamReducer(viewPaneHeader$, activeViewChangedReducer),
    streamReducer(reset$, resetReducer),
  ]
);
